import React, { useRef, useCallback, useState, useMemo } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Button, Row, Col } from 'react-bootstrap';
import { useToast } from '../../../hooks/toast';

import Loading from '../../../components/Loading';
import Input from '../../../components/Input';
import SelectSync from '../../../components/SelectSync';
import getValidationErrors from '../../../utils/getValidationErrors';
import Toggle from '../../../components/Exame/Toggle';
import { optionsTipoModelo } from '../../../utils/options';
import EditorDocumento from '../../../components/EditorDocumento';
import {
  htmlDecode,
  optionsProfissionalModelo,
  optionsPacienteModelo,
  optionsClinicaModelo,
  optionsMensagemConfirmacaModelo,
  optionsMensagemAniversarioModelo,
  optionsMensagemCobrancaModelo,
  TIPO_DOCUMENTO,
  optionsMensagemCartaoVencendoModelo,
  optionsMensagemConviteParticularModelo,
} from '../../../utils/funcoes';

import api from '../../../services/api';
import getResponseErrors from '../../../utils/getResponseErrors';
import { IModelo } from '..';

type Props = {
  modeloMensagem: boolean;
  modelo?: IModelo;
  ModTipo?: number;
  setShowModal(value: boolean): void;
};

const ModeloCadastro: React.FC<Props> = ({
  modelo,
  modeloMensagem,
  ModTipo,
  setShowModal,
}) => {
  const { addToast } = useToast();
  const [modCodigo, setModCodigo] = useState<number | undefined>(
    modelo ? modelo.ModCodigo : undefined,
  );

  const modTipo = useMemo(() => {
    if (modelo) return modelo.ModTipo;
    if (ModTipo) return ModTipo;
    return 0;
  }, [ModTipo, modelo]);

  const tipoLembretevencido =
    modTipo === TIPO_DOCUMENTO.LEMBRETE_VENCIMENTO_CARTAO_WHATSAPP ||
    modTipo === TIPO_DOCUMENTO.LEMBRETE_VENCIDO_5_10_CARTAO_WHATSAPP;

  const optionModelo = useMemo(() => {
    if (modeloMensagem === false) {
      return [
        ...optionsProfissionalModelo,
        ...optionsPacienteModelo,
        ...optionsClinicaModelo,
      ];
    }

    if (modeloMensagem && modTipo === TIPO_DOCUMENTO.CONFIRMACAO_WHATSAPP) {
      return optionsMensagemConfirmacaModelo;
    }

    if (modeloMensagem && modTipo === TIPO_DOCUMENTO.ANIVERSARIO_WHATSAPP) {
      return optionsMensagemAniversarioModelo;
    }

    if (modeloMensagem && modTipo === TIPO_DOCUMENTO.COBRANCA_WHATSAPP) {
      return optionsMensagemCobrancaModelo;
    }

    if (
      modeloMensagem &&
      (modTipo === TIPO_DOCUMENTO.LEMBRETE_VENCIMENTO_CARTAO_WHATSAPP ||
        modTipo === TIPO_DOCUMENTO.LEMBRETE_VENCIDO_5_10_CARTAO_WHATSAPP)
    ) {
      return optionsMensagemCartaoVencendoModelo;
    }

    if (modeloMensagem && modTipo === TIPO_DOCUMENTO.CONVITE_PARTICULAR) {
      return optionsMensagemConviteParticularModelo;
    }

    return [];
  }, [modTipo, modeloMensagem]);

  const formRefModelo = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmitModelo = useCallback(
    async (dados: IModelo) => {
      try {
        setLoading(true);
        formRefModelo.current?.setErrors({});
        const schema = Yup.object().shape({
          ModNome: Yup.string()
            .min(3, 'Mínimo 3 caracteres')
            .required('Nome é obrigatório'),
          ...(!modCodigo && {
            ModTipo: Yup.number().typeError('Selecione o Tipo').required(),
          }),
        });

        const modeloForm = {
          ...dados,
        };
        const ativo = dados.ModAtivo ? dados.ModAtivo.toString() : 'false';

        await schema.validate(modeloForm, {
          abortEarly: false,
        });

        const dadosModelo = {
          ...modeloForm,
          ModNome: modeloForm.ModNome.toUpperCase(),
          ModAtivo: modeloMensagem ? true : ativo === 'true',
          ...(!modCodigo && modeloMensagem === true && { ModTipo }),
        };

        if (modCodigo) {
          await api.put<IModelo>(`/modelo/${modCodigo}`, dadosModelo);
        } else {
          await api.post<IModelo>('/modelo', {
            ...dadosModelo,
            ModModeloMensagem: modeloMensagem,
          });
        }
        setShowModal(false);
        addToast({ title: 'Modelo salvo', type: 'success' });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefModelo.current?.setErrors(errors);
          return;
        }

        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao salvar modelo',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [modCodigo, modeloMensagem, ModTipo, setShowModal, addToast],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <div className="container">
      {loading && <Loading />}

      <Row noGutters>
        <Col md={12}>
          <Form
            ref={formRefModelo}
            initialData={{
              ...modelo,
              ModTipo: modelo?.ModTipo ? modelo.ModTipo.toString() : '0',
              ModTexto: htmlDecode(modelo?.ModTexto || ''),
            }}
            onSubmit={handleSubmitModelo}
          >
            <Row>
              <Col md={9} className="form-group pr-2">
                <Input
                  name="ModNome"
                  label="Nome"
                  maxLength={100}
                  disabled={modeloMensagem}
                />
              </Col>
              {!modCodigo && modeloMensagem === false && (
                <Col md={2} className="form-group pr-2">
                  <SelectSync
                    name="ModTipo"
                    label="Tipo"
                    placeholder="Selecione"
                    handleSelectChange={handleOnChangeVazio}
                    options={optionsTipoModelo}
                  />
                </Col>
              )}
              {modeloMensagem === false && (
                <Col md={1} className="form-group pr-2 pt-2">
                  <Toggle
                    nome="ModAtivo"
                    label="Ativo"
                    classSpam={{ marginTop: '2px' }}
                    isDisabled={false}
                  />
                </Col>
              )}
            </Row>
            <Row className="border-top p-1">
              <Col md="9">
                <EditorDocumento
                  name="ModTexto"
                  height={390}
                  modelo={optionModelo}
                  editorSimples={modeloMensagem}
                />
                <div className="light-grey text-left pt-3">
                  <>
                    <Button type="submit" variant="success" className="mt-2">
                      Salvar dados
                    </Button>
                  </>
                </div>
              </Col>
              <Col md="3">
                <>
                  <h5>Campos automaticos</h5>
                  <table
                    className="table table-hover table-bordered table-sm"
                    style={{ fontSize: 'small' }}
                  >
                    <tbody>
                      {modeloMensagem === false && (
                        <>
                          <tr>
                            <td className="text-center bg-dark text-white">
                              <b>Paciente</b>
                            </td>
                          </tr>
                          {optionsPacienteModelo.map(op => (
                            <tr key={`modeloCadastroPaciente-${op.text}`}>
                              <td>
                                <b>{op.text}</b> {op.label}
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td className="text-center bg-dark text-white">
                              <b>Profissional</b>
                            </td>
                          </tr>
                          {optionsProfissionalModelo.map(op => (
                            <tr key={`modeloCadastroMedico-${op.text}`}>
                              <td>
                                <b>{op.text}</b> {op.label}
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td className="text-center bg-dark text-white">
                              <b>Clinica</b>
                            </td>
                          </tr>
                          {optionsClinicaModelo.map(op => (
                            <tr key={`modeloCadastroClinica-${op.text}`}>
                              <td>
                                <b>{op.text}</b> {op.label}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                      {modeloMensagem &&
                        modTipo === TIPO_DOCUMENTO.CONFIRMACAO_WHATSAPP && (
                          <>
                            <tr>
                              <td className="text-center bg-dark text-white">
                                <b>Dados Agendamento</b>
                              </td>
                            </tr>
                            {optionsMensagemConfirmacaModelo.map(op => (
                              <tr key={`modeloMensagemConfirma-${op.text}`}>
                                <td>
                                  <b>{op.text}</b> {op.label}
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                      {modeloMensagem &&
                        modTipo === TIPO_DOCUMENTO.ANIVERSARIO_WHATSAPP && (
                          <>
                            <tr>
                              <td className="text-center bg-dark text-white">
                                <b>Dados Paciente</b>
                              </td>
                            </tr>
                            {optionsMensagemAniversarioModelo.map(op => (
                              <tr key={`modeloNiverConfirma-${op.text}`}>
                                <td>
                                  <b>{op.text}</b> {op.label}
                                </td>
                              </tr>
                            ))}
                          </>
                        )}

                      {modeloMensagem &&
                        modTipo === TIPO_DOCUMENTO.COBRANCA_WHATSAPP && (
                          <>
                            <tr>
                              <td className="text-center bg-dark text-white">
                                <b>Dados Paciente</b>
                              </td>
                            </tr>
                            {optionsMensagemCobrancaModelo.map(op => (
                              <tr key={`modeloNiverConfirma-${op.text}`}>
                                <td>
                                  <b>{op.text}</b> {op.label}
                                </td>
                              </tr>
                            ))}
                          </>
                        )}

                      {modeloMensagem && tipoLembretevencido && (
                        <>
                          <tr>
                            <td className="text-center bg-dark text-white">
                              <b>Dados Paciente</b>
                            </td>
                          </tr>
                          {optionsMensagemCartaoVencendoModelo.map(op => (
                            <tr key={`modelocartaoVencendo-${op.text}`}>
                              <td>
                                <b>{op.text}</b> {op.label}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}

                      {modeloMensagem &&
                        modTipo === TIPO_DOCUMENTO.CONVITE_PARTICULAR && (
                          <>
                            <tr>
                              <td className="text-center bg-dark text-white">
                                <b>Dados Paciente</b>
                              </td>
                            </tr>
                            {optionsMensagemConviteParticularModelo.map(op => (
                              <tr key={`modeloconviteParticular-${op.text}`}>
                                <td>
                                  <b>{op.text}</b> {op.label}
                                </td>
                              </tr>
                            ))}
                          </>
                        )}
                    </tbody>
                  </table>
                </>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

export default ModeloCadastro;
